<template>
  <div class="orderForm" id="product">
    <!-- Product Selection -->
    <div class="GridCard">
      <el-card
        v-for="product in products"
        :id="product.id"
        :key="product.id"
        :class="[
          'card',
          selectedProduct && selectedProduct.id === product.id
            ? 'isActiveProduct'
            : ''
        ]"
        :body-style="{ padding: '10px', height: '100%' }"
        shadow="never"
      >
        <div @click="selectProduct(product)">
          <img
            :src="product.imageUrl[0]"
            :alt="product.name"
            class="CardImage"
          />
          <div>{{ product.name }}</div>
        </div>
      </el-card>
    </div>

    <!-- ProductVariation Selection -->
    <el-dialog
      :visible.sync="isProductVariationDialogVisible"
      width="66.6%"
      center
      class="hover"
    >
      <template>
        <div slot="title">
          <p>種類（寸法）を選んでください。</p>
        </div>
      </template>
      <el-row :gutter="20">
        <el-col :span="4">
          <img
            :src="selectedProduct ? selectedProduct.imageUrl[0] : ''"
            class="w-img"
          />
        </el-col>
        <el-col :span="20">
          <div class="productForm">
            <el-table
              ref="dialogTable"
              :data="selectedProduct ? selectedProduct.variations : []"
              @row-click="selectProductVariation($event)"
              :row-class-name="productVariationTableRowClassName"
            >
              <el-table-column
                prop="id"
                label="商品コード"
                width="120"
              >
                <template slot-scope="scope">
                  {{ scope.row.id }}
                </template>
              </el-table-column>
              <el-table-column
                prop="size"
                label="種類"
                width="100"
              >
                <template slot-scope="scope">
                  {{ getSizeText(scope.row) }}
                </template>
              </el-table-column>
              <el-table-column
                prop="size"
                label="寸法(cm)"
              >
                <template slot-scope="scope">
                  {{ getDimensionsText(scope.row) }}
                </template>
              </el-table-column>
              <el-table-column
                prop="stockExpression"
                label="在庫状況"
                width="100"
              >
                <template slot-scope="scope">
                  <font-awesome-icon
                    v-if="scope.row.stockExpression == 'IN_STOCK'"
                    :icon="['fal', 'circle']"
                    size="1x"
                  />
                  <font-awesome-icon
                    v-if="scope.row.stockExpression == 'FEW_STOCK'"
                    :icon="['fal', 'triangle']"
                    size="1x"
                  />
                  <font-awesome-icon
                    v-if="scope.row.stockExpression == 'OUT_OF_STOCK'"
                    :icon="['fal', 'times']"
                    size="1x"
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>
      <hr class="divide" />
      <span>
        <el-button
          type="text"
          class="btn-text"
          @click="closeProductVariationDialog()"
          >キャンセル</el-button
        >
      </span>
      <span>
        <el-button
          type="text"
          class="btn-text"
          @click="goToNextStep()"
          :disabled="!selectedProduct || !selectedProductVariation"
          >OK</el-button
        >
      </span>
    </el-dialog>

    <!-- Azukari Product Name Input -->
    <el-dialog
      :visible.sync="isAzukariProductDialogVisible"
      width="400px"
      center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <template>
        <div slot="title">
          <p>商品名と種類を入力してください</p>
        </div>
      </template>
      <el-form :model="productNameForm" :hide-required-asterisk="true">
        <el-form-item
          label="商品名"
          prop="azukariProductName"
          :rules="[{ required: true, message: '名前の入力は必須です' }]"
        >
          <el-input v-model="productNameForm.azukariProductName" />
        </el-form-item>
        <el-form-item
          label="種類"
          prop="azukariProductSize"
        >
          <el-input
            v-model="productNameForm.azukariProductSize"
            placeholder="入力例: 3.5"
          />
        </el-form-item>
      </el-form>
      <div class="DialogActions" slot="footer">
        <el-button
          type="text"
          class="btn-text"
          @click="closeAzukariProductDialog()"
          >キャンセル</el-button
        >
        <el-button
          type="text"
          class="btn-text"
          @click="onClickConfirmAzukariProductNameButton()"
          :disabled="!productNameForm.azukariProductName"
          >OK</el-button
        >
      </div>
    </el-dialog>

    <!-- 本数選択用のモーダル -->
    <el-dialog
      :visible.sync="isProductQuantityDialogVisible"
      width="400px"
      center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <!-- 本数選択: タイトル -->
      <template>
        <div slot="title">
          <p>本数を入力してください</p>
        </div>
      </template>

      <!-- 本数選択 -->
      <div class="flex center">
        <el-form
          :model="productQuantityForm"
          :hide-required-asterisk="true"
        >
          <el-form-item prop="quantity">
            <el-select v-model="productQuantityForm.quantity">
              <el-option
                v-for="item in computedQuantityOptions"
                :key="`quantity-${item}`"
                :label="`${item}本`"
                :value="item"
              />
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <!-- 本数選択: アクション -->
      <div
        slot="footer"
        class="DialogActions"
      >
        <el-button
          type="text"
          class="btn-text"
          @click="closeProductQuantityDialog()"
        >
          キャンセル
        </el-button>
        <el-button
          type="text"
          class="btn-text"
          @click="onClickConfirmProductQuantityButton()"
        >
          OK
        </el-button>
      </div>
    </el-dialog>

    <!-- Loading Screen -->
    <div class="el-loading-mask" v-if="isLoading" style="z-index: 2003;">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from "vuex";
import { produce } from "immer";
import ProductUsecase from "@/usecases/product.usecase";
import { apiErrorHandler } from "@/services/apiErrorHandler";
function isAzukariIhaiProduct (product) {
  return product.category.id === "99000";
}
export default {
  props: {
    selectedCategoryId: {
      type: String,
      default: "",
    },
  },
  data () {
    return {
      productNameForm: {
        azukariProductName: "",
        azukariProductSize: "",
      },
      productQuantityForm: {
        quantity: 1,
      },
      productUsecase: new ProductUsecase(),
      isLoading: false,
      isAzukariProductDialogVisible: false,
      isProductVariationDialogVisible: false,
      isProductQuantityDialogVisible: false,
    };
  },
  computed: {
    ...mapState({
      products: state => state.master.products,
      clientGroupInput: state => state.orderForm.clientGroupInput,
    }),
    ...mapGetters({
      nextStep: "orderForm/nextStep",
    }),
    productInput: {
      get () {
        return this.$store.state.orderForm.productInput || {};
      },
      set (value) {
        this.$store.commit("orderForm/setProductInput", { ...value });
      },
    },
    selectedProduct: {
      get () {
        return this.productInput.product;
      },
    },
    selectedProductVariation: {
      get () {
        return this.productInput.productVariation;
      },
    },
    computedQuantityOptions () {
      const options = [...Array(5)].map((_, i) => i + 1);

      return options;
    },
  },
  async mounted () {
    // 預かり位牌の商品名入力があった場合に復元
    if (this.productInput.azukariProductName) {
      this.productNameForm.azukariProductName = this.productInput.azukariProductName;
    }

    // 預かり位牌の商品名サイズがあった場合に復元
    if (this.productInput.azukariProductSize) {
      this.productNameForm.azukariProductSize = this.productInput.azukariProductSize;
    }

    // 本数選択があった場合に復元
    if (this.productInput.quantity) {
      this.productQuantityForm.quantity = this.productInput.quantity;
    }

    this.isLoading = true;

    try {
      const options = {
        clientGroupId: this.clientGroupInput.id,
        q: {
          categoryId: this.selectedCategoryId,
        },
      };
      const productsInSelectedCategory = await this.productUsecase.getProducts(options);
      this.$store.commit("master/set", {
        key: "products",
        value: productsInSelectedCategory,
      });
    } catch (err) {
      apiErrorHandler(this, err);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    goToNextStep () {
      this.$router.push(this.nextStep);
    },
    productVariationTableRowClassName ({ row }) {
      if (
        this.selectedProductVariation &&
        this.selectedProductVariation.id === row.id
      ) {
        return "current-row";
      } else {
        return "";
      }
    },
    selectProduct (product) {
      this.productInput = produce(this.productInput, d => {
        if (d.product && d.product.id !== product.id) {
          d.productVariation = null;
        }
        d.product = product;
      });

      this.openProductQuantityDialog();
    },
    selectProductVariation (productVariation) {
      this.productInput = produce(this.productInput, d => {
        d.productVariation = productVariation;
      });
    },
    openProductVariationDialog () {
      this.isProductVariationDialogVisible = true;
    },
    closeProductVariationDialog () {
      this.isProductVariationDialogVisible = false;
    },
    openAzukariProductDialog () {
      this.isAzukariProductDialogVisible = true;
    },
    closeAzukariProductDialog () {
      this.isAzukariProductDialogVisible = false;
    },

    /**
     * [openProductQuantityDialog description]
     */
    openProductQuantityDialog () {
      this.isProductQuantityDialogVisible = true;
    },

    /**
     * [openProductQuantityDialog description]
     */
    closeProductQuantityDialog () {
      this.isProductQuantityDialogVisible = false;
    },

    onClickConfirmAzukariProductNameButton () {
      this.productInput = produce(this.productInput, d => {
        d.azukariProductName = this.productNameForm.azukariProductName;
        d.azukariProductSize = this.productNameForm.azukariProductSize;
      });

      this.closeAzukariProductDialog();
      this.openProductVariationDialog();
    },

    /**
     * [onClickConfirmProductQuantityButton description]
     */
    onClickConfirmProductQuantityButton () {
      const { product } = this.productInput;

      this.productInput = produce(this.productInput, d => {
        d.quantity = this.productQuantityForm.quantity;
      });

      this.closeProductQuantityDialog();

      // 預かり位牌の場合は商品名入力のモーダルを表示
      if (isAzukariIhaiProduct(product)) {
        this.openAzukariProductDialog();
      } else {
        this.openProductVariationDialog();
      }
    },

    /**
     * 種類のテキストを生成します。預かり位牌で種類の入力があれば azukariProductSize、それ以外であれば size を出力します。
     * @param   {[type]}  variations  [value description]
     * @return {string} 種類のテキスト
     */
    getSizeText (variations) {
      const { product } = this.productInput;
      const { azukariProductSize } = this.productNameForm;
      const { size } = variations;

      // 預かり位牌の場合
      if (isAzukariIhaiProduct(product)) {
        return azukariProductSize || size;
      }

      return size;
    },

    /**
     * 寸法のテキストを生成します。height, width, depthのどれかがnullであればsizeだけ出力します。
     * @param   {[type]}  variations  [value description]
     * @return {string} 寸法のテキスト
     */
    getDimensionsText (variations) {
      const { height, width, depth } = variations;

      // 位牌サイズの入力がある場合
      if (height && width && depth) {
        return `H${height}×W${width}×D${depth}`;
      }

      return "--";
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/variables.scss";

.el-table--enable-row-hover .el-table__body tr {
  -webkit-tap-highlight-color: transparent;
}
.el-table--enable-row-hover .el-table__body tr.current-row > td {
  background-color: $blue-light !important;
}
.el-table--enable-row-hover .el-table__body tr.current-row:hover > td {
  background-color: $blue-light !important;
}
.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: transparent !important;
}
.productForm .cell {
  margin-left: 1rem !important;
  font-size: 16px !important;
  padding: 0 !important;
}
.productForm .el-table__row {
  cursor: pointer;
}
.d-text {
  font-size: 16px;
  text-align: left;
}
.b-text {
  font-size: 14px;
  text-align: left;
}
.p-text {
  text-align: center;
  font-size: 18px;
  border: 2px solid $green-base;
}
.orderForm .el-col-4 {
  width: 40% !important;
}
.orderForm .el-dialog--center .el-dialog__b14pxody {
  padding: 25px 25px 10px !important;
  word-break: break-word;
}
.divide {
  margin-left: -25px;
  margin-right: -25px;
  margin-top: 2.5rem;
}
.orderForm.el-button--text {
  color: black !important;
}
.orderForm .el-button--text {
  color: black;
}
.elDialog .el-table .cell {
  font-weight: 500 !important ;
  line-height: 20px !important;
  font-size: 14px;
}
.card .el-dialog__headerbtn .el-dialog__close {
  color: white;
}
.orderForm .el-col-20 {
  width: 60%;
}
.orderForm .el-col-5 {
  width: 24.83333%;
  text-align: left;
}
.elRow {
  margin-left: 19px !important;
  margin-right: -50px !important;
}
@media (max-width: 1024px) {
  .elRow {
    margin-left: 5px !important;
    margin-right: -10px !important;
  }
  #product-id .el-table__body,
  .el-table__footer,
  .el-table__header {
    width: 160px !important;
  }
}
@media (max-width: 768px) {
  #product-id .el-table__body,
  .el-table__footer,
  .el-table__header {
    width: 130px !important;
  }
}
.w-img {
  width: 100%;
}
.orderForm .el-dialog__title {
  font-weight: 1000 !important;
}
.btn-text {
  width: 50%;
}
.button-color {
  border: 1.5px solid $blue-deep;
  color: $blue-deep;
}
.cardStyle {
  border: 2px solid $--color-primary;
  position: absolute;
}
.current {
  background: white;
}
#product .el-dialog__header {
  font-size: 18px;
  color: $text-light;
  font-weight: bold;
}
#product-id .el-table thead {
  color: $blue-base !important;
}
#product-id .el-table .cell {
  text-align: center;
  font-size: 12px;
}
#product-id .el-button.is-disabled,
#product-id .el-button.is-disabled:focus,
#product-id .el-button.is-disabled:hover {
  color: $text-extra-light !important;
  background-color: $background-gray-deep !important;
  border-color: $--border-color-light !important;
}
</style>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

$--gap: 25px;

.inner {
  display: flex;
  height: 80%;
}
.leftColumn {
  margin-right: 10px;
}
.leftColumn,
.ProductMeta {
  flex: 1;
}
.leftColumn__thumbnail {
  width: 100%;
}
.ProductNumber {
  background-color: $background-dark-light;
  color: white;
  padding: 3px;
}
.description {
  font-size: 12px;
  color: $background-dark-light;
}
.categories::after {
  content: "";
  flex: auto;
}
.card__image {
  width: 100%;
}
.title {
  font-weight: bold;
}
.description {
  color: gray;
}
.fix-card {
  height: -webkit-fill-available;
}
.ChooseProductButton {
  margin-top: 10px;
  width: 100%;
}
.product-button {
  color: #d6d5d5 !important;
}
.isActiveProduct {
  border: 3px solid $blue-deep;
}
.tab {
  pointer-events: none;
}
/*.GridCardForHome {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: $--gap: 25px;
  margin: $--gap: 25px;
  text-align: center;
}*/
.GridCard {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: $--gap;
  margin: $--gap;
  text-align: center;
}
/*.GridCardForAdminHome {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  grid-gap: 10px;
  margin: 20px;
}*/
.categories::after {
  content: "";
  flex: auto;
}
.title {
  font-weight: bold;
}
.isActiveProduct {
  border: 3px solid $blue-deep;
}
.CardImage {
  width: 100%;
}
.productNumber {
  font-weight: bold;
}
.DialogActions {
  display: flex;
  border-top: 1px solid $--border-color-light;
  padding: 10px 0;
}
</style>
